export type UUID = string;

export enum InterviewPhase {
  INITIATED = "INITIATED",
  ICEBREAKER = "ICEBREAKER",
  HR = "HR",
  PROJECTS = "PROJECTS",
  SKILLS = "SKILLS",
  COMPETENCY = "COMPETENCY",
  FEEDBACK = "FEEDBACK",
  COMPLETED = "COMPLETED",
}

export enum SessionStatus {
  IN_PROGRESS = "IN PROGRESS",
  FAILED = "FAILED",
  COMPLETED = "COMPLETED",
  SCORED = "SCORED",
  PENDING = "PENDING",
}

export interface InterviewHistoryItem {
  role: "bot" | "candidate";
  content: string;
  audio_s3_key?: string;
  transcript_confirmed: boolean;
}

export interface InterviewSession {
  session_id: string;
  candidate_id: string;
  email_address: string;
  institute_id?: string;
  session_name?: string;
  first_name?: string;
  last_name?: string;
  course?: string;
  grade?: string | null;
  admission_year?: string | null;
  current_phase?: InterviewPhase;
  current_topic_index?: number;
  current_depth?: number;
  max_depth?: number;
  history?: InterviewHistoryItem[];
  topics?: string[];
  round_type?: string;
  round_id?: string;
  is_completed: boolean;
  created_at: string;
  last_updated: string;
  status: SessionStatus;
  reason?: string;
  score?: number | null;
  submitted_at?: string | null;
}

export interface InterviewSessionListResponse {
  sessions: InterviewSession[];
  no_of_records: number;
}

export interface StartInterviewRequest {
  file_url: string;
  round_id?: string;
}

export interface StartInterviewResponse {
  session_id: string;
  status: "PENDING";
  detail?: string;
  error?: string;
}

export interface IcebreakerResponse {
  status: "PENDING" | "READY" | "FAILED";
  icebreaker_text?: string;
  audio_url?: string;
  reason?: string;
}

export interface ParseResumeRequest {
  file_url: string;
}

export interface ParseResumeResponse {
  resume_text: string;
}

export interface TranscribeResponse {
  transcript: string;
  audio_s3_key: string;
}

export interface AnswerRequest {
  confirmed_answer: string;
  audio_s3_key?: string;
  is_inactivity?: boolean;
}

export interface AnswerResponse {
  decision: "PROBE" | "PIVOT" | "FINISH";
  next_question?: string;
  audio_url?: string;
  confidence_score?: number;
  message?: string;
  report_summary?: string;
}

export interface ResumeInterviewResponse {
  status: "RESUMED" | "COMPLETED";
  session_id?: string;
  next_question?: string;
  audio_url?: string;
  history?: InterviewHistoryItem[];
  current_phase?: InterviewPhase;
  message?: string;
  report_summary?: string;
  duration?: number;
}

export interface SessionFeedback {
  technical_readiness_score: number;
  technical_readiness_justification: string;
  communication_style_evaluation: string;
  key_strengths: string[];
  areas_for_improvement: string[];
  final_verdict: string;
  transcript_summary: string;
}

export interface SkillScorecard {
  skill: string;
  initial_scenario: string;
  outcome: string;
  nudges_count: number;
  score: number;
}

export interface TechnicalSkillsEvaluation {
  technical_scorecard: SkillScorecard[];
  coachability_score: number;
  recruiter_note: string;
}

export enum CompetencyType {
  STRATEGIC_LOGIC = "Strategic Logic",
  CONSTRAINT_RESOLUTION = "Constraint Resolution",
  INFORMATION_DISCOVERY = "Information Discovery",
  SELF_CORRECTION = "Self-Correction",
}

export interface CompetencyEvaluation {
  competency: CompetencyType;
  scenario_asked: string;
  communication_flag: "High Articulation" | "Low Comprehension" | "Standard";
  problem_solving_notes: string;
  resilience_notes: string;
  professional_notes: string;
  implementation_notes: string;
  score: number;
}

export interface CoreCompetenciesReport {
  competency_evaluations: CompetencyEvaluation[];
  candidate_superpower: string;
  growth_area: string;
  communication_tip: string;
  overall_competency_score: number;
}

export interface BehavioralEvaluation {
  initial_reaction: number;
  stakeholder_awareness: number;
  resolution_logic: number;
  behavioral_tags: string[];
  bias_indicators: string[];
  justification: string;
}

export interface HREvaluation {
  motivation_score: number;
  self_awareness_score: number;
  operational_fit_score: number;
  hr_narrative: string;
}

export interface HMEvaluation {
  impact_velocity_score: number;
  managerial_friction_score: number;
  hm_recommendation: string;
}

export enum RoundType {
  TECHNICAL = "TECHNICAL",
  BEHAVIORAL = "BEHAVIORAL",
  HR = "HR",
  HIRING_MANAGER = "HIRING_MANAGER",
}

export interface InterviewRound {
  round_id: string;
  name: string;
  description: string;
  round_type: RoundType;
  created_at: string;
}

export interface InterviewRoundListResponse {
  rounds: InterviewRound[];
}

export interface FeedbackResponse {
  session_id: string;
  session_feedback: SessionFeedback;
  resume_score: ResumeScores;
  resume_improvement: ResumeImprovement[];
  resume_text: string;
  scored_at: string;
  technical_skills_evaluation?: TechnicalSkillsEvaluation;
  core_competencies_report?: CoreCompetenciesReport;
  behavioral_evaluation?: BehavioralEvaluation;
  hr_evaluation?: HREvaluation;
  hm_evaluation?: HMEvaluation;
  round_type?: RoundType;
  session_name: string; // Add this
  first_name: string; // Add this
  last_name: string; // Add this
}

export interface SessionHistoryItem {
  role: "bot" | "candidate" | string;
  content: string;
  audio_s3_key?: string | null;
  transcript_confirmed?: boolean;
}

export interface FeedbackApiResponse {
  feedback: FeedbackResponse;
  session_history?: SessionHistoryItem[];
}

export interface ResumeScores {
  readability: number;
  impact: number;
  conciseness: number;
  tech_clarity: number;
  total: number;
  actionable_improvements: string[];
}

export interface ResumeImprovement {
  original: string;
  improvement: string;
  reason: string;
}

export interface ResumeGradeRequest {
  resume_data: string;
}

export interface ResumeGradeResponse {
  scores: ResumeScores;
  improvements: ResumeImprovement[];
}

// ─── Resume Grading (standalone feature) ───────────────────────────────────

export enum GradeResumeStatus {
  INITIATED = "INITIATED",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
}

export interface GradeResumeSession {
  session_id: string;
  candidate_id: string;
  email_address: string;
  institute_id?: string;
  session_name?: string;
  first_name?: string;
  last_name?: string;
  course?: string;
  grade?: string | null;
  admission_year?: string | null;
  resume_text?: string | null;
  created_at: string;
  last_updated: string;
  reason?: string | null;
  status: GradeResumeStatus;
  score?: number | null;
  resume_score?: ResumeScores | null;
  resume_improvement?: ResumeImprovement[] | null;
}

export interface GradeResumeSessionListResponse {
  grade_sessions: GradeResumeSession[];
  no_of_records: number;
}

/** POST /grade_resume → { session_id, status: "Pending" } */
export interface GradeResumeStartResponse {
  session_id: string;
  status: "Pending";
}

/** GET /grade_resume?session_id=... */
export type GradeResumeResultResponse =
  | { session_id: string; status: "Pending" }
  | { session_id: string; status: "Failed"; reason: string }
  | { session_id: string; resume_grade: GradeResumeSession };

// ─── Resume Optimization & JD Matching ─────────────────────────────────────

export enum OptimizeResumeStatus {
  INITIATED = "INITIATED",
  FAILED = "FAILED",
  COMPLETED = "COMPLETED",
}

export enum BulletStatus {
  PROPOSED = "PROPOSED",
  ACCEPTED = "ACCEPTED",
  REJECTED = "REJECTED",
  EDITED = "EDITED",
  COACHING = "COACHING",
  COMPLETED = "COMPLETED",
}

export interface StrategicModification {
  original_point: string;
  rewrite_suggestion: string;
  reasoning: string;
  status: BulletStatus;
}

export interface JDMatchResponse {
  match_score: number;
  hard_skills: string[];
  soft_skills: string[];
  missing_keywords: string[];
  screening_prediction: "Yes" | "No" | "Maybe";
  strategic_modifications: StrategicModification[];
}

export interface BulletOptimizationState {
  bullet_id: string;
  original_bullet: string;
  ai_suggestion?: string;
  user_edit?: string;
  final_bullet?: string;
  status: BulletStatus;
  chat_history: InterviewHistoryItem[];
  jd_focus_keywords: string[];
}

export interface ResumeOptimizationSession {
  session_id: string;
  candidate_id: string;
  institute_id?: string;
  job_description: string;
  resume_url: string;
  created_at: string;
  last_updated: string;
  status: OptimizeResumeStatus;
  reason?: string;
  match_score: number;
  total_modifications: number;
  completed_modifications: number;
  result?: JDMatchResponse;
  bullet_states: Record<string, BulletOptimizationState>;
}

export interface ResumeOptimizationListResponse {
  optimization_sessions: ResumeOptimizationSession[];
  no_of_records: number;
}

/** POST /interview/jd_match */
export interface JDMatchRequest {
  job_description: string;
  resume_url: string;
}

/** POST /interview/bullet/action */
export interface BulletActionRequest {
  session_id: string;
  bullet_id: string;
  action: "ACCEPT" | "REJECT" | "EDIT";
  user_edit?: string;
}

/** POST /interview/bullet/chat */
export interface BulletChatRequest {
  session_id: string;
  bullet_id: string;
  user_input: string;
}

export interface BulletActionResponse {
  status: "SUCCESS" | "FAILED";
  bullet_state: BulletOptimizationState;
}

export type OptimizeResumeResultResponse =
  | { session_id: string; status: OptimizeResumeStatus.INITIATED }
  | { session_id: string; status: OptimizeResumeStatus.FAILED; reason: string }
  | {
      session_id: string;
      status: OptimizeResumeStatus.COMPLETED;
      result: JDMatchResponse;
      resume_optimization: ResumeOptimizationSession;
    };
